import { applyExpense, cardPeriodForDate, isTrackedCard, knownCardPeriodForExpense, planExpense } from "./finance";
import { cardPaymentEventId } from "./card-payments";
import { assertAllocationsWithinNu, snapshotForDate } from "./periods";
import { installmentSchedule, isInstallmentDraft, scheduledInstallmentMovements } from "./installments";
import type { ExpenseCommand, ExpenseDraft, FinancialSnapshot, Movement, ReceiptPayload } from "./types";

export class ExpenseCommitError extends Error {
  constructor(
    message: string,
    public readonly code: "missing_snapshot" | "rejected" | "needs_reconfirmation",
  ) {
    super(message);
    this.name = "ExpenseCommitError";
  }
}

export function createExpenseCommand(
  draft: ExpenseDraft,
  approvedSavingsRescueCents: number,
  receipt?: ReceiptPayload,
): ExpenseCommand {
  const expenseId = `GASTO_${crypto.randomUUID()}`;
  const installmentCount = installmentSchedule(draft).length;
  const installmentMovementIds = installmentCount > 1
    ? Array.from({ length: installmentCount }, (_, index) => index === 0
      ? expenseId
      : `${expenseId}_MSI_${String(index + 1).padStart(2, "0")}`)
    : undefined;
  return {
    id: `EXPENSE_COMMAND_${expenseId}`,
    type: "expense",
    expenseId,
    savingsMovementId: (draft.savingsContributionCents ?? 0) > 0 ? `AHORRO_COMPRA_${expenseId}` : undefined,
    rescueMovementId: approvedSavingsRescueCents > 0 ? `RESCATE_GASTO_${expenseId}` : undefined,
    installmentMovementIds,
    draft,
    approvedSavingsRescueCents,
    createdAt: new Date().toISOString(),
    receipt,
  };
}

export async function commitExpenseCommand(
  uid: string,
  command: ExpenseCommand,
): Promise<{ snapshot: FinancialSnapshot; movements: Movement[] }> {
  const [{ doc, runTransaction }, { firestore }] = await Promise.all([
    import("firebase/firestore"),
    import("./firebase"),
  ]);
  const snapshotReference = doc(firestore, "users", uid, "snapshots", "current");
  const expenseReference = doc(firestore, "users", uid, "movements", command.expenseId);

  return runTransaction(firestore, async (transaction) => {
    const [snapshotDocument, existingExpense] = await Promise.all([
      transaction.get(snapshotReference),
      transaction.get(expenseReference),
    ]);
    if (!snapshotDocument.exists()) throw new ExpenseCommitError("No encontré el saldo actual de Banculem.", "missing_snapshot");
    const current = snapshotForDate({ ...(snapshotDocument.data() as FinancialSnapshot), source: "firestore" as const });
    if (existingExpense.exists()) return { snapshot: current, movements: [] };

    const trackedCard = isTrackedCard(command.draft.paymentMethod);
    const cardPeriod = command.draft.cardPeriod
      ?? knownCardPeriodForExpense(current, command.draft.paymentMethod, command.draft.spentOn);
    if (trackedCard && !cardPeriod) {
      throw new ExpenseCommitError("No pude determinar el periodo de corte de esta tarjeta. Actualiza y vuelve a capturar el gasto.", "rejected");
    }
    if (trackedCard && cardPeriod) {
      const schedule = installmentSchedule(command.draft);
      const cutDay = Number(command.draft.cardCutDay);
      const cardPeriods = schedule.length
        ? schedule.map((item) => cardPeriodForDate(item.dueOn, cutDay))
        : [cardPeriod];
      if (cardPeriods.some((period) => !period)) {
        throw new ExpenseCommitError("No pude calcular todos los cortes de las parcialidades. Revisa la tarjeta y vuelve a intentarlo.", "rejected");
      }
      const uniquePeriods = [...new Set(cardPeriods as string[])];
      const paymentDocuments = await Promise.all(uniquePeriods.map((period) => transaction.get(
        doc(firestore, "users", uid, "cardPaymentEvents", cardPaymentEventId(command.draft.paymentMethod, period)),
      )));
      if (paymentDocuments.some((paymentDocument) => paymentDocument.exists())) {
        throw new ExpenseCommitError("Ese corte ya fue pagado. El gasto no se guardó para evitar alterar un estado de cuenta cerrado.", "rejected");
      }
    }

    const draft = cardPeriod ? { ...command.draft, cardPeriod } : command.draft;

    const plan = planExpense(current, draft);
    if (!plan.allowed) {
      const missing = plan.missingCents ? ` Faltan ${(plan.missingCents / 100).toLocaleString("es-MX", { style: "currency", currency: "MXN" })}.` : "";
      throw new ExpenseCommitError(`El saldo cambió y el gasto ya no puede guardarse.${missing}`, "rejected");
    }
    if (plan.savingsRescueCents > command.approvedSavingsRescueCents) {
      throw new ExpenseCommitError("El saldo semanal cambió y ahora sería necesario tomar más ahorro. Revísalo otra vez.", "needs_reconfirmation");
    }

    const ids = {
      expenseId: command.expenseId,
      savingsMovementId: command.savingsMovementId,
      rescueMovementId: command.rescueMovementId,
      installmentMovementIds: command.installmentMovementIds,
    };
    const applied = applyExpense(current, draft, plan, new Date(command.createdAt), ids);
    const committedIds = new Set([
      command.expenseId,
      command.savingsMovementId,
      command.rescueMovementId,
      ...(command.installmentMovementIds ?? []),
    ].filter((id): id is string => Boolean(id)));
    const nextSnapshot: FinancialSnapshot = assertAllocationsWithinNu({
      ...applied,
      source: "firestore",
      movements: applied.movements.map((movement) => committedIds.has(movement.id)
        ? { ...movement, syncState: "synced" as const }
        : movement),
    });
    const movementIds = new Set(committedIds);
    const scheduled = isInstallmentDraft(draft) ? scheduledInstallmentMovements({
      draft,
      purchaseId: command.expenseId,
      movementIds: command.installmentMovementIds ?? [],
      purchasePeriodId: current.periodLabel ?? "periodo-actual",
      createdAt: command.createdAt,
      syncState: "synced",
      periodForDate: (spentOn) => cardPeriodForDate(spentOn, Number(draft.cardCutDay)),
    }) : [];
    if (isInstallmentDraft(draft) && scheduled.length !== Number(draft.installmentCount) - 1) {
      throw new ExpenseCommitError("No pude crear todas las parcialidades. El gasto no se guardó.", "rejected");
    }
    const movements = [
      ...nextSnapshot.movements.filter((movement) => movementIds.has(movement.id)),
      ...scheduled,
    ];

    for (const movement of movements) {
      transaction.set(doc(firestore, "users", uid, "movements", movement.id), movement);
    }
    if (command.receipt) {
      transaction.set(doc(firestore, "users", uid, "receipts", command.expenseId), {
        id: command.expenseId,
        name: command.receipt.name,
        mimeType: command.receipt.mimeType,
        base64: command.receipt.base64,
        createdAt: command.createdAt,
      });
    }
    transaction.set(snapshotReference, nextSnapshot);
    return { snapshot: nextSnapshot, movements };
  });
}

export async function readCloudReceipt(uid: string, receiptId: string): Promise<Blob | null> {
  const [{ doc, getDoc }, { firestore }] = await Promise.all([
    import("firebase/firestore"),
    import("./firebase"),
  ]);
  const receiptDocument = await getDoc(doc(firestore, "users", uid, "receipts", receiptId));
  if (!receiptDocument.exists()) return null;
  const value = receiptDocument.data() as { base64?: string; mimeType?: string };
  if (!value.base64 || !String(value.mimeType ?? "").startsWith("image/")) return null;
  const binary = atob(value.base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type: value.mimeType });
}

export async function readCloudSnapshot(uid: string): Promise<FinancialSnapshot> {
  const [{ doc, getDoc }, { firestore }] = await Promise.all([
    import("firebase/firestore"),
    import("./firebase"),
  ]);
  const document = await getDoc(doc(firestore, "users", uid, "snapshots", "current"));
  if (!document.exists()) throw new Error("No encontré el estado financiero en Firebase.");
  return snapshotForDate({ ...(document.data() as FinancialSnapshot), source: "firestore" });
}

export async function subscribeToCloudSnapshot(
  uid: string,
  onSnapshotValue: (snapshot: FinancialSnapshot) => void,
  onError: (error: Error) => void,
): Promise<() => void> {
  const [{ doc, onSnapshot }, { firestore }] = await Promise.all([
    import("firebase/firestore"),
    import("./firebase"),
  ]);

  return onSnapshot(
    doc(firestore, "users", uid, "snapshots", "current"),
    (documentSnapshot) => {
      if (!documentSnapshot.exists()) return;
      const value = documentSnapshot.data() as FinancialSnapshot;
      if (value.schemaVersion === 3) onSnapshotValue(snapshotForDate({ ...value, source: "firestore" }));
    },
    (error) => onError(error),
  );
}
